<template>
  <div class="supplier-form">
    <form class="vertical">
      <section>
        <h1>{{ $t('suppliers.labels.details') }}</h1>
        <div class="supplier-details-form-container">
          <div class="form-control">
            <label>{{ $t('suppliers.details.name') }}</label>
            <InputField v-model="formData.name" type="text" class="bordered" @change="onChange" />
          </div>
          <div class="form-control">
            <label>{{ $t('suppliers.details.clientName') }}</label>
            <tol-type-ahead
              :search-params="{
                url: '/assignments/user-quick-search',
                searchKeys: ['name'],
                objectType: 'client'
              }"
              class="blue bordered"
              menu-label="Search Client"
              @select-item="onSelectClient($event)"
            >
              <template v-slot="{ value }">
                {{ value.name }}
              </template>
            </tol-type-ahead>
            {{ formData.client_name }}
          </div>

          <div class="break" />
          <div class="form-control">
            <label>{{ $t('suppliers.details.gst') }}</label>
            <InputField v-model="formData.gst" type="text" class="bordered" @change="onChange" />
          </div>
          <div class="form-control">
            <label>{{ $t('suppliers.details.qst') }}</label>
            <InputField v-model="formData.qst" type="text" class="bordered" @change="onChange" />
          </div>
          <div class="form-control">
            <label>{{ $t('suppliers.details.hst') }}</label>
            <InputField v-model="formData.hst" type="text" class="bordered" @change="onChange" />
          </div>
        </div>
        <div class="break" />

        <div class="form-control">
          <label>{{ $t('suppliers.details.is_active') }}</label>
          <checkbox size="tiny" :checked="formData.is_active" @change="updateSupplierProfile()" />
        </div>
      </section>
      <section class="details-section">
        <div class="form-control">
          <div class="add">
            <h1>{{ $t('suppliers.labels.addresses') }}</h1>
            <span @click="addSupplierAddress">+</span>
          </div>
          <div class="address-section">
            <div
              v-for="(address, index) in formData.address"
              :key="address.id"
              class="address-space"
            >
              <tol-address
                :address="address"
                :editable="true"
                :default-id="formData.address_id"
                @change="updateAddress(index, $event)"
                @delete="deleteAddress(index, $event)"
              />
            </div>
          </div>
        </div>
      </section>

      <div class="module-action-container">
        <tol-button
          id="save-btn"
          button-type="save"
          label="Save"
          :class="[
            'module-action',
            { disabled: !isOnScreenDataModified && formData.reference_number }
          ]"
          @click="doSupplierSave(formData.reference_number ? 'SAVE' : 'CREATE')"
        >
        </tol-button>
      </div>
    </form>
  </div>
</template>
<script>
import { clone } from 'ramda';
import InputField from '@/app/components/InputField';
import Checkbox from '@/app/components/Checkbox';
import TolAddress from '@/app/components/TolAddress';
import TolButton from '@/app/components/TolButton';
import { SupplierDetailsActions } from '../suppliers-details.keys';
import TolTypeAhead from '@/app/components/TolTypeAhead';
import { TolErrorsGetters } from '@/app/features/tol-errors/tol-errors.keys';

export default {
  name: 'SuppliersDetailsForm',

  components: {
    InputField,
    Checkbox,
    TolAddress,
    TolButton,
    TolTypeAhead
  },

  props: {
    supplier: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      formData: {},
      isAddressFormVisible: false,
      isOnScreenDataModified: false,
      isProcessFormBtnClicked: false,
      newAddressCount: 0
    };
  },
  computed: {
    currentSaveStatus() {
      return this.$store.getters[TolErrorsGetters.HasErrors] ? 'error' : 'success';
    }
  },
  watch: {
    supplier: {
      immediate: true,
      handler() {
        this.formData = clone(this.supplier);
      }
    }
  },

  methods: {
    onChange() {
      this.isOnScreenDataModified = true;
    },
    addSupplierAddress() {
      this.isOnScreenDataModified = true;
      let newAddress = {
        id: 'newAddressId' + this.newAddressCount++,
        line1: null,
        line2: null,
        city: null,
        postal_code: null,
        province_abbr: null
      };
      this.formData.address.push(newAddress);
    },
    updateAddress(index, event) {
      this.isOnScreenDataModified = true;
      if (event.name === 'is_default') {
        this.formData.address_id = event.value;
      } else {
        let field = event.name;
        this.formData.address[index][field] = event.value;
      }
    },
    updateSupplierProfile() {
      this.isOnScreenDataModified = true;
      this.formData.is_active = !this.formData.is_active;
    },

    onSelectClient(event) {
      this.isOnScreenDataModified = true;
      this.formData.client_name = event.name;
      this.formData.heldcheque_client_id = event.id;
    },
    deleteAddress(index, event) {
      this.isOnScreenDataModified = true;

      if (event.id == this.formData.address_id) {
        this.formData.address_id = null;
      }

      this.formData.address.splice(index, 1);
    },

    doSupplierSave(action) {
      this.isProcessFormBtnClicked = true;
      if (this.isOnScreenDataModified) {
        this.$store.dispatch(SupplierDetailsActions.SaveSupplierDetails, {
          supplierData: this.formData,
          action
        });
      }

      window.setTimeout(() => {
        this.isProcessFormBtnClicked = false;
        this.isOnScreenDataModified = false;
      }, 2000);
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
@import '@/app/styles/action-buttons.scss';

.supplier-details-form-container {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  width: 70%;

  & div {
    width: 20vw;
  }
}
.supplier-form {
  form {
    width: 100%;
    section {
      margin-bottom: 20px;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      background-color: $background-level-3;

      .details-section {
        padding-bottom: 25px;

        h1 {
          border-bottom: 1px solid $row-border;
        }
      }

      h1 {
        padding-top: 15px;
        border-bottom: 1px solid $row-border;
      }
      h1,
      h2 {
        flex: 0 100%;
      }

      .break {
        width: 100%;
        height: 0;
      }

      .form-control {
        padding: 10px 0;
        margin: 1em;
        flex: 1;
        > label {
          display: block;
          font-weight: bold;
        }
        .address-section {
          display: flex;
          flex-wrap: wrap;
        }
      }
      .add {
        display: flex;
        justify-content: flex-end;
        span {
          display: inline-block;
          font-size: 1.2em;
          font-weight: bold;
          width: 20px;
          height: 20px;
          line-height: 20px;
          border-radius: 100%;
          text-align: center;
          &:hover {
            cursor: pointer;
            background-color: $light-grey-2;
          }
          &.history {
            display: none;
          }
        }
      }
    }

    .form-input {
      border-bottom: 1px solid $blue;
      font-size: 1em;
      height: inherit;
      &:focus {
        outline: none;
      }
    }

    .bordered {
      border: 1px solid $blue;
      font-size: 1em;
      &:focus {
        outline: none;
      }
    }

    .module-action-container {
      justify-content: flex-end;
      /*display: flex;
      position: relative;
      justify-content: space-between;
      background-color: $background-level-3;
      padding: 60px 20px;
      width: 100%;
      margin: 30px 0;
      .supplier-save {
        width: 50px;
        height: 50px;
        padding: 0;
        font-size: 1.5em;
        margin-right: 20px;
        color: $white;
        border-radius: 100%;
        background-color: $secondary-green;
        &.disabled {
          background-color: $light-grey-2;
          color: $light-grey-1;
          &:hover {
            background-color: $light-grey-2;
            color: $light-grey-1;
            cursor: not-allowed;
          }
        }
      }*/
    }
  }

  .nowrap {
    white-space: nowrap;
  }

  .address-space {
    background-color: white;
    margin: 10px;
  }
}
</style>
